import { type ReactNode, useEffect, useId, useRef } from 'react';

import { IconButton } from '@/components/ui/IconButton';
import { cn } from '@/utils/cn';

import styles from './Dialog.module.css';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Buttons at the bottom. A submit button can target a form in the body with `form={id}`. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  children?: ReactNode;
}

/**
 * A modal on the native <dialog> element: the browser traps focus, Esc closes it, and the
 * page behind is inert. Content mounts only while open, so a form inside starts fresh each time.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  children,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const pressedOnBackdrop = useRef(false);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={cn(styles.dialog, styles[size])}
      aria-labelledby={titleId}
      aria-describedby={description === undefined ? undefined : descriptionId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      // Close on a click that starts and ends on the backdrop — not on a text selection that
      // happens to end outside the panel.
      onMouseDown={(event) => {
        pressedOnBackdrop.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (pressedOnBackdrop.current && event.target === event.currentTarget) onClose();
      }}
    >
      {open && (
        <div className={styles.panel}>
          <header className={styles.header}>
            <div className={styles.heading}>
              <h2 id={titleId} className={styles.title}>
                {title}
              </h2>
              {description !== undefined && (
                <p id={descriptionId} className={styles.description}>
                  {description}
                </p>
              )}
            </div>
            <IconButton icon="close" label="Close" variant="ghost" onClick={onClose} />
          </header>
          <div className={styles.body}>{children}</div>
          {footer !== undefined && <footer className={styles.footer}>{footer}</footer>}
        </div>
      )}
    </dialog>
  );
}
